import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { IGrowthOverview } from 'core';

interface IGrowthLevelBar {
  growth: IGrowthOverview;
}

/**
 * Очки и уровень (ТЗ 0039 шаг 4).
 *
 * Очки нигде не хранятся — это сумма по выданным медалям, и полоса считается
 * от порогов. На последнем уровне `nextLevelXp` приходит `null`, и полоса
 * показывается полной: «до следующего 0» было бы неправдой, следующего нет.
 */
export const GrowthLevelBar = memo(({ growth }: IGrowthLevelBar) => {
  const { t } = useTranslation();
  const isMax = growth.nextLevelXp === null;
  // 🔴 Считаем от ПОРОГА ТЕКУЩЕГО уровня, а не от нуля. Раньше студент со 135
  // очками на уровне с порогом 130 (следующий — 210) видел заполнение 64%
  // вместо 6%: полоса говорила «почти дошёл» там, где он только начал.
  const span = isMax ? 0 : (growth.nextLevelXp ?? 0) - growth.levelFloorXp;
  const percent =
    isMax || span <= 0
      ? 100
      : Math.min(100, Math.max(0, Math.round(((growth.xp - growth.levelFloorXp) / span) * 100)));

  return (
    <Card>
      <Row>
        {/* Подпись уровня — константа кода, а не контент справочника, поэтому
            локализуется здесь. Серверная остаётся запасной: новый порог,
            для которого ключ ещё не завели, покажется по-русски, а не путём
            ключа i18n. */}
        <Level>{t(`growth.level.${growth.level}`, { defaultValue: growth.levelTitle })}</Level>
        <Xp>{t('growth.xp', { n: growth.xp })}</Xp>
      </Row>
      <Track>
        <Fill style={{ width: `${percent}%` }} />
      </Track>
      <Hint>
        {isMax ? t('growth.maxLevel') : t('growth.toNextLevel', { n: growth.toNextLevel })}
      </Hint>
    </Card>
  );
});

const Card = styled.div`
  padding: 20px 24px;
  border-radius: 12px;
  background: ${Colors.bg_gray};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const Level = styled.span`
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Xp = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Track = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${Colors.gray};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: ${Colors.main};
  transition: width 0.3s ease;
`;

const Hint = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;
